/**
 * ReplayScrubberPane — Interactive replay timeline controller
 *
 * REFINEMENTS (V1):
 * - Horizontal timeline scrubber with draggable position indicator
 * - Continuity state markers along timeline
 * - Replay window visualization with temporal focus
 * - Step-by-step navigation (prev/next event)
 * - Preserve proof_only semantics (read-only navigation only)
 * - Deterministic rendering with semantic colors
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type { ReplayComparePaneModel, ContinuityPaneModel, TimelinePaneModel } from '../../modules/governanceEventStream/operationalRendering/types.js';
import { continuityToColor, SEMANTIC, NEUTRAL, TYPOGRAPHY, SPACING } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface ReplayScrubberPaneProps {
  replayModel: ReplayComparePaneModel;
  continuityModel: ContinuityPaneModel;
  timelineModel: TimelinePaneModel;
  onPositionChange?: (timestamp: string) => void;
  onStepForward?: () => void;
  onStepBackward?: () => void;
}

const SCRUBBER_HEIGHT = 48;
const MARKER_SIZE = 12;
const HANDLE_SIZE = 16;

function formatCompactTime(timestamp: string): string {
  if (!timestamp) return '—';
  return timestamp.slice(11, 23);
}

export const ReplayScrubberPane: React.FC<ReplayScrubberPaneProps> = ({
  replayModel,
  continuityModel,
  timelineModel,
  onPositionChange,
  onStepForward,
  onStepBackward,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredTimestamp, setHoveredTimestamp] = useState<string | null>(null);
  const scrubberRef = useRef<HTMLDivElement>(null);

  // Calculate timeline range
  const timelineRange = useMemo(() => {
    if (!timelineModel.earliestTimestamp || !timelineModel.latestTimestamp) {
      return null;
    }
    const start = new Date(timelineModel.earliestTimestamp).getTime();
    const end = new Date(timelineModel.latestTimestamp).getTime();
    const duration = end - start;
    return { start, end, duration };
  }, [timelineModel.earliestTimestamp, timelineModel.latestTimestamp]);

  // Calculate replay window position
  const windowPosition = useMemo(() => {
    if (!timelineRange || !replayModel.windowStart || !replayModel.windowEnd) {
      return null;
    }
    const windowStart = new Date(replayModel.windowStart).getTime();
    const windowEnd = new Date(replayModel.windowEnd).getTime();
    const leftPercent = ((windowStart - timelineRange.start) / timelineRange.duration) * 100;
    const widthPercent = ((windowEnd - windowStart) / timelineRange.duration) * 100;
    return { leftPercent, widthPercent };
  }, [timelineRange, replayModel.windowStart, replayModel.windowEnd]);

  // Calculate continuity transition positions
  const continuityMarkers = useMemo(() => {
    if (!timelineRange) return [];
    return continuityModel.rows.map((row) => {
      const timestamp = new Date(row.timestamp).getTime();
      const positionPercent = ((timestamp - timelineRange.start) / timelineRange.duration) * 100;
      const color = continuityToColor(row.currentStatus);
      return {
        transitionId: row.transitionId,
        timestamp: row.timestamp,
        positionPercent,
        previousStatus: row.previousStatus,
        currentStatus: row.currentStatus,
        color,
      };
    });
  }, [timelineRange, continuityModel.rows]);

  // Calculate current position (based on selected event or window center)
  const currentPosition = useMemo(() => {
    if (!timelineRange) return null;
    // Use window center as current position
    if (replayModel.windowStart && replayModel.windowEnd) {
      const windowStart = new Date(replayModel.windowStart).getTime();
      const windowEnd = new Date(replayModel.windowEnd).getTime();
      const center = (windowStart + windowEnd) / 2;
      return ((center - timelineRange.start) / timelineRange.duration) * 100;
    }
    return 50; // Default to center
  }, [timelineRange, replayModel.windowStart, replayModel.windowEnd]);

  const handleScrubberClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrubberRef.current || !timelineRange) return;
    const rect = scrubberRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = (x / rect.width) * 100;
    const timestamp = new Date(timelineRange.start + (percent / 100) * timelineRange.duration).toISOString();
    onPositionChange?.(timestamp);
  }, [timelineRange, onPositionChange]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrubberRef.current || !timelineRange || !isDragging) return;
    const rect = scrubberRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const percent = (x / rect.width) * 100;
    const timestamp = new Date(timelineRange.start + (percent / 100) * timelineRange.duration).toISOString();
    onPositionChange?.(timestamp);
  }, [timelineRange, isDragging, onPositionChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    handleScrubberClick(e);
  }, [handleScrubberClick]);

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    }
  }, [isDragging]);

  if (!timelineRange) {
    return (
      <div style={{
        padding: SPACING.sp3,
        backgroundColor: NEUTRAL.bg600,
        borderBottom: `1px solid ${NEUTRAL.border}`,
        color: NEUTRAL.text300,
        fontSize: TYPOGRAPHY.caption.size,
      }}>
        No timeline data available for replay scrubber
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: NEUTRAL.bg600,
      borderBottom: `1px solid ${NEUTRAL.border}`,
      padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
    }}>
      {/* Header with controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: SPACING.sp2,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.sp3,
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text400,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}>
            Replay Scrubber
          </span>
          {replayModel.windowId && (
            <span style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: SEMANTIC.replayWindow.text,
              backgroundColor: SEMANTIC.replayWindow.chip,
              padding: '1px 6px',
              borderRadius: 3,
            }}>
              {replayModel.totalInWindow} events in window
            </span>
          )}
        </div>

        {/* Step controls */}
        <div style={{
          display: 'flex',
          gap: SPACING.sp2,
        }}>
          <button
            onClick={onStepBackward}
            disabled={!onStepBackward}
            style={{
              padding: '2px 8px',
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: 3,
              backgroundColor: NEUTRAL.bg500,
              color: onStepBackward ? NEUTRAL.text500 : NEUTRAL.text300,
              cursor: onStepBackward ? 'pointer' : 'not-allowed',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
            type="button"
          >
            ◀ PREV
          </button>
          <button
            onClick={onStepForward}
            disabled={!onStepForward}
            style={{
              padding: '2px 8px',
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: 600,
              border: `1px solid ${NEUTRAL.border}`,
              borderRadius: 3,
              backgroundColor: NEUTRAL.bg500,
              color: onStepForward ? NEUTRAL.text500 : NEUTRAL.text300,
              cursor: onStepForward ? 'pointer' : 'not-allowed',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
            type="button"
          >
            NEXT ▶
          </button>
        </div>
      </div>

      {/* Scrubber timeline */}
      <div
        ref={scrubberRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setHoveredTimestamp(null)}
        style={{
          position: 'relative',
          height: SCRUBBER_HEIGHT,
          backgroundColor: NEUTRAL.bg300,
          borderRadius: 3,
          overflow: 'hidden',
          cursor: 'pointer',
          border: `1px solid ${NEUTRAL.borderSubtle}`,
        }}
      >
        {/* Timeline track */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: NEUTRAL.bg200,
        }} />

        {/* Replay window highlight */}
        {windowPosition && (
          <div style={{
            position: 'absolute',
            left: `${windowPosition.leftPercent}%`,
            width: `${windowPosition.widthPercent}%`,
            top: 0,
            bottom: 0,
            backgroundColor: SEMANTIC.replayWindow.bg,
            borderLeft: `1px solid ${SEMANTIC.replayWindow.border}`,
            borderRight: `1px solid ${SEMANTIC.replayWindow.border}`,
            opacity: 0.6,
          }} />
        )}

        {/* Continuity transition markers */}
        {continuityMarkers.map((marker) => (
          <div
            key={marker.transitionId}
            style={{
              position: 'absolute',
              left: `${marker.positionPercent}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: MARKER_SIZE,
              height: MARKER_SIZE,
              borderRadius: '50%',
              backgroundColor: marker.color.text,
              border: `2px solid ${marker.color.border}`,
              boxShadow: `0 0 6px ${marker.color.text}40`,
              cursor: 'pointer',
              zIndex: 2,
            }}
            title={`${marker.previousStatus} → ${marker.currentStatus} at ${formatCompactTime(marker.timestamp)}`}
            onMouseEnter={() => setHoveredTimestamp(marker.timestamp)}
            onMouseLeave={() => setHoveredTimestamp(null)}
          />
        ))}

        {/* Current position indicator */}
        {currentPosition !== null && (
          <div style={{
            position: 'absolute',
            left: `${currentPosition}%`,
            top: 0,
            bottom: 0,
            width: 2,
            backgroundColor: NEUTRAL.text600,
            transform: 'translateX(-50%)',
            zIndex: 3,
          }}>
            {/* Handle */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              borderRadius: '50%',
              backgroundColor: NEUTRAL.text600,
              border: `2px solid ${NEUTRAL.bg600}`,
              boxShadow: `0 0 8px ${NEUTRAL.text600}60`,
            }} />
          </div>
        )}

        {/* Hover tooltip */}
        {hoveredTimestamp && (
          <div style={{
            position: 'absolute',
            top: -28,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: NEUTRAL.bg700,
            border: `1px solid ${NEUTRAL.border}`,
            borderRadius: 3,
            padding: `${SPACING.sp1}px ${SPACING.sp2}px`,
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text300,
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}>
            {formatCompactTime(hoveredTimestamp)}
          </div>
        )}
      </div>

      {/* Timeline labels */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: SPACING.sp1,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text300,
        fontFamily: TYPOGRAPHY.mono.family,
      }}>
        <span>{formatCompactTime(timelineModel.earliestTimestamp || '')}</span>
        <span>{formatCompactTime(timelineModel.latestTimestamp || '')}</span>
      </div>
    </div>
  );
};
